package com.siriusxm.unifiedcontent.console.aggregator

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.State
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import com.siriusxm.pia.ApplicationContext
import com.siriusxm.pia.CapabilityQuery
import com.siriusxm.pia.resolveCapabilities
import kotlinx.coroutines.CancellationException

/**
 * Resolve a set of AVP questions in one batched `/auth-check` call and expose the answers as Compose
 * state. Reads null until AVP answers, then a map from each query to whether it is allowed.
 *
 * Used where one screen needs many related answers at once, such as one question per partial or one
 * per relationship row, so they cost one round trip rather than one each. Keyed on the set of queries,
 * so a changed list re-checks; already-answered queries come from the [ApplicationContext.capabilities]
 * cache. Fails closed: if the check throws, every query reads as denied. A query whose check failed is
 * asked again a few times while this stays in composition, and the map updates if it is then allowed
 * (UCA-1281); see [resolveCapabilities].
 *
 * Hide-only. The aggregator re-checks every one of these on the request itself.
 */
@Composable
internal fun ApplicationContext.rememberCapabilities(
    queries: Collection<CapabilityQuery>
): State<Map<CapabilityQuery, Boolean>?> {
    val key = queries.toSet()
    val decisions = remember(key) { mutableStateOf<Map<CapabilityQuery, Boolean>?>(null) }
    LaunchedEffect(key) {
        if (key.isEmpty()) {
            decisions.value = emptyMap()
            return@LaunchedEffect
        }
        try {
            capabilities.resolveCapabilities(key) { answers ->
                decisions.value = key.associateWith { answers[it] == true }
            }
        } catch (e: CancellationException) {
            throw e
        } catch (e: Throwable) {
            console.error("AVP capability check failed; treating the queries as denied", e)
            if (decisions.value == null) decisions.value = key.associateWith { false }
        }
    }
    return decisions
}
